/**
 * Flint Product Configurator - JavaScript
 * 
 * Handles dynamic variant selection, image updates, pricing,
 * and progressive disclosure for visual product customization.
 * 
 * Features:
 * - Visual swatch selection
 * - Real-time variant matching
 * - Dynamic image updates with crossfade
 * - Price calculations with deltas
 * - Progressive disclosure (optional)
 * - Inventory awareness
 * - AJAX add to cart
 * - Accessibility support
 */

class FlintConfigurator {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.container = document.querySelector(`#flint-configurator-${sectionId}`);
    
    if (!this.container) {
      console.warn(`Configurator section ${sectionId} not found`);
      return;
    }

    // Load configuration data
    this.loadData();
    
    // Initialize state
    this.currentSelections = {};
    this.basePrice = this.product.variants[0].price;
    
    // Initialize UI
    this.init();
  }

  /**
   * Load product and settings data from JSON
   */
  loadData() {
    const dataElement = document.getElementById(`configurator-data-${this.sectionId}`);
    if (!dataElement) {
      console.error('Configurator data not found');
      return;
    }

    try {
      const data = JSON.parse(dataElement.textContent);
      this.product = data.product;
      this.settings = data.settings;
    } catch (error) {
      console.error('Failed to parse configurator data:', error);
    }
  }

  /**
   * Initialize configurator
   */
  init() {
    // Cache DOM elements
    this.cacheElements();
    
    // Set initial selections (first value of each option)
    this.initializeSelections();
    
    // Bind event listeners
    this.bindEvents();
    
    // Initialize progress indicator
    if (this.settings.progressiveDisclosure) {
      this.initializeProgressiveDisclosure();
    }
    
    // Initialize progress dots
    this.initializeProgress();
    
    // Set initial state
    this.updateVariant();
    
    console.log('✅ Flint Configurator initialized', {
      product: this.product.title,
      variants: this.product.variants.length
    });
  }

  /**
   * Cache frequently accessed DOM elements
   */
  cacheElements() {
    this.elements = {
      mainImage: document.getElementById(`configurator-main-image-${this.sectionId}`),
      price: document.getElementById(`configurator-price-${this.sectionId}`),
      priceDelta: document.getElementById(`configurator-price-delta-${this.sectionId}`),
      variantId: document.getElementById(`configurator-variant-id-${this.sectionId}`),
      addToCart: document.getElementById(`configurator-add-to-cart-${this.sectionId}`),
      availability: document.getElementById(`configurator-availability-${this.sectionId}`),
      progress: document.getElementById(`configurator-progress-${this.sectionId}`),
      summary: document.getElementById(`configurator-summary-${this.sectionId}`),
      quantityInput: document.getElementById(`quantity-selector-${this.sectionId}`),
      quantityHidden: document.getElementById(`configurator-quantity-${this.sectionId}`),
      optionGroups: this.container.querySelectorAll('.flint-configurator__option-group'),
      swatches: this.container.querySelectorAll('.flint-configurator__swatch')
    };
  }

  /**
   * Initialize default selections
   */
  initializeSelections() {
    this.product.options.forEach((optionName, index) => {
      const position = index + 1;
      const firstValue = this.getFirstOptionValue(position);
      this.currentSelections[position] = firstValue;
    });
  }

  /**
   * Get first value for an option position
   */
  getFirstOptionValue(position) {
    const swatch = this.container.querySelector(`[data-option-position="${position}"]`);
    return swatch ? swatch.dataset.value : null;
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Swatch click events
    this.elements.swatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => this.handleSwatchClick(e));
      swatch.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleSwatchClick(e);
        }
      });
    });

    // Quantity input sync
    if (this.elements.quantityInput && this.elements.quantityHidden) {
      this.elements.quantityInput.addEventListener('input', (e) => {
        const value = Math.max(1, parseInt(e.target.value) || 1);
        e.target.value = value;
        this.elements.quantityHidden.value = value;
      });
    }

    // Add to cart button
    this.elements.addToCart.addEventListener('click', (e) => this.handleAddToCart(e));

    // Shopify section editor re-render
    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId === this.sectionId) {
        this.init();
      }
    });
  }

  /**
   * Handle swatch click
   */
  handleSwatchClick(event) {
    const swatch = event.currentTarget;
    
    // Don't allow selection of unavailable options
    if (swatch.classList.contains('is-unavailable')) {
      return;
    }

    const position = parseInt(swatch.dataset.optionPosition);
    const value = swatch.dataset.value;

    // Update selection
    this.currentSelections[position] = value;

    // Update UI
    this.updateSwatchStates(position);
    this.updateVariant();

    // Progressive disclosure: unlock next group
    if (this.settings.progressiveDisclosure) {
      this.unlockNextGroup(position);
    }

    // Update progress
    this.updateProgress();
  }

  /**
   * Update swatch visual states
   */
  updateSwatchStates(position) {
    const swatches = this.container.querySelectorAll(`[data-option-position="${position}"]`);
    
    swatches.forEach(swatch => {
      if (swatch.dataset.value === this.currentSelections[position]) {
        swatch.classList.add('is-selected');
        swatch.setAttribute('aria-pressed', 'true');
      } else {
        swatch.classList.remove('is-selected');
        swatch.setAttribute('aria-pressed', 'false');
      }
    });
  }

  /**
   * Find matching variant based on current selections
   */
  findMatchingVariant() {
    return this.product.variants.find(variant => {
      return variant.option1 === this.currentSelections[1] &&
             (variant.option2 === this.currentSelections[2] || !this.currentSelections[2]) &&
             (variant.option3 === this.currentSelections[3] || !this.currentSelections[3]);
    });
  }

  /**
   * Update variant (image, price, availability)
   */
  updateVariant() {
    const variant = this.findMatchingVariant();
    
    if (!variant) {
      console.warn('No matching variant found', this.currentSelections);
      return;
    }

    // Update hidden variant ID
    if (this.elements.variantId) {
      this.elements.variantId.value = variant.id;
    }

    // Update main image
    this.updateImage(variant);

    // Update price
    this.updatePrice(variant);

    // Update availability
    this.updateAvailability(variant);

    // Update inventory-aware options
    if (this.settings.inventoryFiltering) {
      this.updateInventoryFiltering();
    }

    // Update summary
    this.updateSummary();

    // Emit custom event for other components
    this.emitVariantChange(variant);
  }

  /**
   * Update main product image with crossfade
   */
  updateImage(variant) {
    if (!this.elements.mainImage || !variant.featured_image) {
      return;
    }

    const currentSrc = this.elements.mainImage.src;
    const newSrc = variant.featured_image;

    // Don't update if image is the same
    if (currentSrc === newSrc) {
      return;
    }

    // Crossfade animation
    this.elements.mainImage.classList.add('is-transitioning');

    setTimeout(() => {
      this.elements.mainImage.src = newSrc;
      this.elements.mainImage.alt = variant.title;
      
      // Remove transition class after image loads
      this.elements.mainImage.onload = () => {
        this.elements.mainImage.classList.remove('is-transitioning');
      };
    }, 300);
  }

  /**
   * Update price display with optional delta
   */
  updatePrice(variant) {
    if (!this.elements.price) {
      return;
    }

    const newPrice = variant.price;
    const formattedPrice = this.formatMoney(newPrice);

    this.elements.price.textContent = formattedPrice;

    // Show price delta if enabled
    if (this.settings.showPriceDeltas && this.elements.priceDelta) {
      this.updatePriceDelta(newPrice);
    }
  }

  /**
   * Update price delta (+£200, -£50, etc.)
   */
  updatePriceDelta(newPrice) {
    const delta = newPrice - this.basePrice;

    if (delta === 0) {
      this.elements.priceDelta.style.display = 'none';
      return;
    }

    const formattedDelta = this.formatMoney(Math.abs(delta));
    const sign = delta > 0 ? '+' : '-';
    
    this.elements.priceDelta.textContent = `${sign}${formattedDelta}`;
    this.elements.priceDelta.style.display = 'inline-flex';

    // Update styling based on increase/decrease
    this.elements.priceDelta.classList.remove('is-increase', 'is-decrease');
    this.elements.priceDelta.classList.add(delta > 0 ? 'is-increase' : 'is-decrease');
  }

  /**
   * Update availability status
   */
  updateAvailability(variant) {
    if (!this.elements.availability) {
      return;
    }

    const isAvailable = variant.available;

    // Update text
    const statusText = this.elements.availability.querySelector('span');
    if (statusText) {
      statusText.textContent = isAvailable ? 'In Stock' : 'Out of Stock';
    }

    // Update icon color (via CSS class on parent)
    this.elements.availability.classList.toggle('is-available', isAvailable);
    this.elements.availability.classList.toggle('is-unavailable', !isAvailable);

    // Enable/disable add to cart button
    if (this.elements.addToCart) {
      this.elements.addToCart.disabled = !isAvailable;
    }
  }

  /**
   * Disable swatches that lead to out-of-stock variants
   */
  updateInventoryFiltering() {
    this.product.options.forEach((optionName, index) => {
      const position = index + 1;
      const swatches = this.container.querySelectorAll(`[data-option-position="${position}"]`);

      swatches.forEach(swatch => {
        const value = swatch.dataset.value;
        
        // Create temporary selection with this value
        const testSelections = { ...this.currentSelections, [position]: value };
        
        // Check if any variant exists with these selections
        const hasAvailableVariant = this.product.variants.some(variant => {
          const matches = 
            variant.option1 === testSelections[1] &&
            (variant.option2 === testSelections[2] || !testSelections[2]) &&
            (variant.option3 === testSelections[3] || !testSelections[3]);
          
          return matches && variant.available;
        });

        // Add/remove unavailable class
        swatch.classList.toggle('is-unavailable', !hasAvailableVariant);
      });
    });
  }

  /**
   * Initialize progressive disclosure (lock later groups)
   */
  initializeProgressiveDisclosure() {
    this.elements.optionGroups.forEach((group, index) => {
      if (index > 0) {
        group.classList.add('is-locked');
      } else {
        group.classList.add('is-active');
      }
    });
  }

  /**
   * Unlock next option group
   */
  unlockNextGroup(currentPosition) {
    const nextGroup = this.container.querySelector(`[data-option-index="${currentPosition + 1}"]`);
    
    if (nextGroup) {
      nextGroup.classList.remove('is-locked');
      nextGroup.classList.add('is-active');

      // Scroll into view
      nextGroup.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    // Mark current group as completed
    const currentGroup = this.container.querySelector(`[data-option-index="${currentPosition}"]`);
    if (currentGroup) {
      currentGroup.classList.remove('is-active');
      currentGroup.classList.add('is-completed');
    }
  }

  /**
   * Initialize progress indicator dots
   */
  initializeProgress() {
    if (!this.elements.progress) {
      return;
    }

    // Clear existing dots
    this.elements.progress.innerHTML = '';

    // Create dot for each option
    this.product.options.forEach((optionName, index) => {
      const dot = document.createElement('div');
      dot.className = 'progress-dot';
      dot.dataset.optionIndex = index + 1;
      
      if (index === 0) {
        dot.classList.add('is-active');
      }
      
      this.elements.progress.appendChild(dot);
    });
  }

  /**
   * Update progress indicator
   */
  updateProgress() {
    if (!this.elements.progress) {
      return;
    }

    const dots = this.elements.progress.querySelectorAll('.progress-dot');
    
    dots.forEach((dot, index) => {
      const position = index + 1;
      const hasSelection = !!this.currentSelections[position];
      
      dot.classList.toggle('is-completed', hasSelection);
      dot.classList.toggle('is-active', 
        hasSelection && !this.currentSelections[position + 1]
      );
    });
  }

  /**
   * Update configuration summary
   */
  updateSummary() {
    if (!this.elements.summary) {
      return;
    }

    const summaryContent = this.elements.summary.querySelector('.flint-configurator__summary-content');
    if (!summaryContent) {
      return;
    }

    // Build summary HTML
    let html = '';
    this.product.options.forEach((optionName, index) => {
      const position = index + 1;
      const value = this.currentSelections[position];
      
      if (value) {
        html += `
          <div class="summary-item">
            <span class="summary-label">${optionName}:</span>
            <span class="summary-value">${value}</span>
          </div>
        `;
      }
    });

    summaryContent.innerHTML = html;
    
    // Show summary if all options selected
    const allSelected = this.product.options.every((_, index) => {
      return !!this.currentSelections[index + 1];
    });
    
    this.elements.summary.style.display = allSelected ? 'block' : 'none';
  }

  /**
   * Handle add to cart
   */
  async handleAddToCart(event) {
    event.preventDefault();

    const variantId = this.elements.variantId.value;
    const quantity = parseInt(this.elements.quantityHidden?.value || 1);
    
    if (!variantId) {
      console.error('No variant selected');
      return;
    }

    // Show loading state
    this.elements.addToCart.disabled = true;
    this.elements.addToCart.innerHTML = '<span>Adding...</span>';

    try {
      // Add to cart via Shopify AJAX API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const data = await response.json();
      
      // Success! Open cart drawer or redirect
      this.handleAddToCartSuccess(data);

    } catch (error) {
      console.error('Add to cart error:', error);
      this.handleAddToCartError(error);
    }
  }

  /**
   * Handle successful add to cart
   */
  handleAddToCartSuccess(data) {
    console.log('✅ Added to cart:', data);

    // Reset button
    this.elements.addToCart.disabled = false;
    this.elements.addToCart.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M3 3H5L7 13H17L19 6H6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <circle cx="8" cy="17" r="1" fill="currentColor"/>
        <circle cx="16" cy="17" r="1" fill="currentColor"/>
      </svg>
      <span>Add to Cart</span>
    `;

    // CRITICAL FIX: Refresh cart drawer content from server
    // This ensures the drawer shows the newly added item
    setTimeout(() => {
      this.refreshCartDrawerContent();
    }, 300);
    
    // Open drawer after content is refreshed
    setTimeout(() => {
      this.openCartDrawer();
      
      // Update counter after drawer opens
      setTimeout(() => {
        this.updateCartCount();
      }, 400);
    }, 800);

    // Emit success event
    document.dispatchEvent(new CustomEvent('flint:cartAdded', {
      detail: { variantId: data.id, quantity: data.quantity }
    }));
  }

  /**
   * Refresh cart drawer HTML from server
   * This solves the empty cart bug by fetching updated drawer content
   */
  refreshCartDrawerContent() {
    console.log('🎯 Refreshing cart drawer content from server');
    
    // Find the cart drawer element
    const cartDrawer = document.querySelector('cart-drawer, .cart-drawer, [data-cart-drawer]');
    if (!cartDrawer) {
      console.log('🎯 No cart drawer found in DOM');
      return;
    }
    
    // Fetch the full page HTML to get updated cart drawer
    fetch(window.location.href)
      .then(response => response.text())
      .then(html => {
        // Parse the HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Find the cart drawer in the fetched HTML
        const newCartDrawer = doc.querySelector('cart-drawer, .cart-drawer, [data-cart-drawer]');
        
        if (newCartDrawer) {
          // Replace the entire cart drawer with the updated version
          cartDrawer.innerHTML = newCartDrawer.innerHTML;
          console.log('🎯 Cart drawer content refreshed successfully');
          
          // Update counter immediately after drawer refresh
          setTimeout(() => {
            console.log('🎯 Updating counter after drawer refresh');
            
            // Get the cart count first before showing
            fetch('/cart.js')
              .then(response => response.json())
              .then(cart => {
                // Fix the counter bubble parent
                const cartBubbles = document.querySelectorAll('.cart-bubble');
                cartBubbles.forEach(bubble => {
                  bubble.classList.remove('visually-hidden');
                  bubble.classList.remove('hidden');
                });
                
                // Fix and UPDATE the counter elements
                const counterElements = document.querySelectorAll('.cart-count, [data-cart-count], .cart-bubble__text-count, [data-testid="cart-bubble"]');
                counterElements.forEach(el => {
                  el.textContent = cart.item_count;
                  el.classList.remove('hidden');
                  el.style.display = 'inline-block';
                  el.style.visibility = 'visible';
                  el.style.opacity = '1';
                });
                
                console.log('🎯 Counter updated:', cart.item_count);
              })
              .catch(err => console.log('🎯 Counter update failed:', err));
          }, 50);
        } else {
          console.log('🎯 Could not find cart drawer in fetched HTML');
        }
      })
      .catch(error => {
        console.error('🎯 Error refreshing cart drawer:', error);
      });
  }

  /**
   * Update cart count in header
   */
  updateCartCount() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const counterElements = document.querySelectorAll('.cart-count, [data-cart-count], .cart-bubble__text-count, [data-testid="cart-bubble"]');
        counterElements.forEach(el => {
          el.textContent = cart.item_count;
          el.classList.remove('hidden');
          el.style.display = 'inline-block';
          el.style.visibility = 'visible';
        });
        console.log('🎯 Cart count updated:', cart.item_count);
      })
      .catch(error => {
        console.error('🎯 Failed to update cart count:', error);
      });
  }

  /**
   * Open cart drawer (compatible with theme)
   */
  openCartDrawer() {
    // Method 1: Custom cart drawer component (like product hero)
    const cartDrawerComponent = document.querySelector('cart-drawer-component');
    if (cartDrawerComponent) {
      if (typeof cartDrawerComponent.open === 'function') {
        cartDrawerComponent.open();
        return;
      }
      if (cartDrawerComponent.hasAttribute('open') === false) {
        cartDrawerComponent.setAttribute('open', '');
        cartDrawerComponent.dispatchEvent(new Event('open'));
        return;
      }
    }

    // Method 2: Standard cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.add('active');
      return;
    }

    // Method 3: Simulate click on cart icon
    const cartIcon = document.querySelector('[data-cart-icon], .cart-icon, #cart-icon');
    if (cartIcon) {
      cartIcon.click();
      return;
    }

    // Fallback: Show success message
    alert('Product added to cart!');
  }

  /**
   * Handle add to cart error
   */
  handleAddToCartError(error) {
    console.error('❌ Add to cart failed:', error);

    // Reset button
    this.elements.addToCart.disabled = false;
    this.elements.addToCart.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M3 3H5L7 13H17L19 6H6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <circle cx="8" cy="17" r="1" fill="currentColor"/>
        <circle cx="16" cy="17" r="1" fill="currentColor"/>
      </svg>
      <span>Add to Cart</span>
    `;

    // Show error message
    alert('Sorry, we couldn\'t add this product to your cart. Please try again.');
  }

  /**
   * Emit variant change event
   */
  emitVariantChange(variant) {
    document.dispatchEvent(new CustomEvent('flint:variantChanged', {
      detail: {
        sectionId: this.sectionId,
        variant: variant,
        selections: this.currentSelections
      }
    }));
  }

  /**
   * Format money (simple version - uses Shopify's default format)
   */
  formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return `£${dollars}`;
    // Note: In production, use Shopify's theme.Currency or theme.formatMoney
  }
}

/**
 * Initialize all configurators on page
 */
function initializeFlintConfigurators() {
  const configuratorElements = document.querySelectorAll('[id^="flint-configurator-"]');
  
  configuratorElements.forEach(element => {
    const sectionId = element.dataset.sectionId;
    if (sectionId) {
      new FlintConfigurator(sectionId);
    }
  });
}

/**
 * Auto-initialize when DOM is ready
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeFlintConfigurators);
} else {
  initializeFlintConfigurators();
}

/**
 * Re-initialize when Shopify section editor changes
 */
document.addEventListener('shopify:section:load', function(event) {
  const sectionId = event.detail.sectionId;
  if (sectionId) {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      new FlintConfigurator(sectionId);
    }, 100);
  }
});

/**
 * Smooth scroll to configurator from hero button
 */
document.addEventListener('click', function(event) {
  const scrollBtn = event.target.closest('[data-scroll-to]');
  if (scrollBtn) {
    event.preventDefault();
    const targetSelector = scrollBtn.dataset.scrollTo;
    const targetElement = document.querySelector(targetSelector);
    
    if (targetElement) {
      targetElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FlintConfigurator;
}
